import React, { useState, useEffect } from 'react';
import { TOKENS } from '../tokens.js';
import { api } from '../api.js';
import { Tape, Stamp, TicketDivider } from './primitives.jsx';

const SESSION_KEY = 'andy-session-roles';

function readRoles() {
  try { return JSON.parse(sessionStorage.getItem(SESSION_KEY) || '{}'); }
  catch { return {}; }
}
function writeRole(role) {
  const r = readRoles(); r[role] = Date.now();
  sessionStorage.setItem(SESSION_KEY, JSON.stringify(r));
}

export default function PasswordGate({ role, onExit, urlParams = {}, hint, children }) {
  const [authed, setAuthed] = useState(() => !!readRoles()[role]);
  const [input, setInput] = useState('');
  const [err, setErr] = useState(false);
  const [busy, setBusy] = useState(false);
  const [autoTrying, setAutoTrying] = useState(false);

  useEffect(() => {
    if (authed) return;
    if (role !== 'watch') return;
    const pw = urlParams.pw;
    if (!pw) return;
    setAutoTrying(true);
    setBusy(true);
    api.auth('watch', pw.trim())
      .then(() => {
        writeRole('watch');
        setAuthed(true);
        try { window.history.replaceState(null, '', '#watch'); } catch {}
      })
      .catch(() => { setErr(true); setTimeout(() => setErr(false), 1800); })
      .finally(() => { setBusy(false); setAutoTrying(false); });
  }, [role, urlParams.pw, authed]);

  const tryUnlock = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await api.auth(role, input.trim());
      writeRole(role);
      setAuthed(true);
    } catch {
      setErr(true);
      setTimeout(() => setErr(false), 1800);
    } finally {
      setBusy(false);
    }
  };

  if (authed) return children;

  if (autoTrying) {
    return (
      <div className="paper-bg" style={{ minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 32 }}>
        <div style={{ textAlign: 'center' }}>
          <div className="hand" style={{ fontSize: 28, color: TOKENS.maroon, marginBottom: 10 }}>letting you in.</div>
          <div style={{ width: 48, height: 48, margin: '0 auto', borderRadius: '50%', border: `3px solid ${TOKENS.maroon}`, borderTopColor: 'transparent', animation: 'spin 900ms linear infinite' }} />
          <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
        </div>
      </div>
    );
  }

  return (
    <div className="paper-bg" style={{ minHeight: '100vh', position: 'relative', overflow: 'hidden' }}>
      <Tape style={{ top: 20, left: '6%', transform: 'rotate(-12deg)', opacity: 0.85 }} />
      <Tape variant="maroon" style={{ top: 28, right: '8%', transform: 'rotate(8deg)', opacity: 0.8 }} />
      <Tape variant="stone" style={{ bottom: 60, left: '4%', transform: 'rotate(-22deg)', opacity: 0.5 }} />
      <Tape style={{ bottom: 36, right: '12%', transform: 'rotate(18deg)', opacity: 0.55 }} />

      <div style={{
        maxWidth: 560, margin: '0 auto',
        padding: 'clamp(36px, 8vw, 64px) clamp(20px, 5vw, 36px) 60px',
        display: 'flex', flexDirection: 'column', alignItems: 'center',
        textAlign: 'center', position: 'relative',
      }}>
        <div style={{ position: 'relative', marginBottom: 14 }}>
          <Stamp color={TOKENS.maroon} rotate={-6} style={{ fontSize: 12 }}>
            Delivered · {new Date().getFullYear() === 2026 ? "May '26" : 'May 2026'}
          </Stamp>
        </div>

        <div className="hand" style={{
          fontSize: 'clamp(26px, 5vw, 34px)',
          color: TOKENS.maroon,
          transform: 'rotate(-2deg)',
          marginBottom: 4,
          lineHeight: 1,
        }}>
          official.
        </div>

        <h1 className="display" style={{
          fontSize: 'clamp(56px, 13vw, 96px)',
          fontStyle: 'italic',
          fontWeight: 500,
          margin: '0 0 10px',
          lineHeight: 0.92,
          color: TOKENS.ink,
        }}>
          He nailed it.
        </h1>

        <div style={{
          fontSize: 13, letterSpacing: '0.24em', textTransform: 'uppercase',
          color: TOKENS.ink70, marginBottom: 28,
        }}>
          Virginia Tech · Class of 2026
        </div>

        <div style={{
          position: 'relative',
          background: '#fdfbf4',
          padding: 'clamp(16px, 4vw, 22px) clamp(20px, 5vw, 30px) clamp(40px, 6vw, 52px)',
          maxWidth: 460,
          width: '100%',
          transform: 'rotate(-0.6deg)',
          boxShadow: '0 2px 6px rgba(0,0,0,0.10), 0 18px 50px rgba(0,0,0,0.10)',
          marginBottom: 36,
        }}>
          <Tape variant="maroon" style={{ top: -14, left: '50%', marginLeft: -42, transform: 'rotate(-3deg)' }} />
          <img
            src="/hokie-andy.png"
            alt=""
            style={{ width: '70%', maxWidth: 240, display: 'block', margin: '0 auto 8px' }}
          />
          <div className="hand" style={{
            fontSize: 'clamp(22px, 4vw, 28px)',
            color: TOKENS.maroonDark,
            transform: 'rotate(-1deg)',
            lineHeight: 1.1,
          }}>
            25 notes made it through.
          </div>
        </div>

        <p className="serif" style={{
          fontSize: 'clamp(16px, 3vw, 18px)',
          color: TOKENS.ink,
          lineHeight: 1.55,
          margin: '0 0 28px',
          maxWidth: 420,
        }}>
          Andy graduated, your messages were delivered, and the montage is ready. Drop in the password and watch them all in one go.
        </p>

        <form onSubmit={tryUnlock} style={{ width: '100%', maxWidth: 360 }}>
          <input
            autoFocus
            type="password"
            value={input}
            onChange={(e) => setInput(e.target.value)}
            placeholder="password"
            disabled={busy}
            style={{
              width: '100%', padding: '16px 20px', fontSize: 18,
              background: '#fff', border: `2px solid ${TOKENS.ink}`,
              borderRadius: 10, textAlign: 'center', outline: 'none',
              letterSpacing: '0.1em', fontFamily: 'Rubik',
              animation: err ? 'shake 0.4s' : 'none',
              boxSizing: 'border-box',
            }}
          />
          {err && <div style={{ color: TOKENS.maroon, marginTop: 10, fontSize: 14 }}>nope, not quite. try again.</div>}
          <button type="submit" className="btn primary" style={{ marginTop: 18, width: '100%' }} disabled={busy || !input.trim()}>
            {busy ? 'Checking.' : 'Press play →'}
          </button>
          {hint && <div className="hand" style={{ marginTop: 28, fontSize: 20, color: TOKENS.ink70, opacity: 0.75, transform: 'rotate(-1deg)' }}>
            {hint}
          </div>}
        </form>

        <div style={{ width: '100%', maxWidth: 420, marginTop: 12 }}>
          <TicketDivider label="11 min 33 sec, 25 voices, one Andy" />
        </div>

        <div style={{ display: 'flex', gap: 14, flexWrap: 'wrap', justifyContent: 'center' }}>
          <Stamp color={TOKENS.orangeDark} rotate={-3}>Family</Stamp>
          <Stamp color={TOKENS.stone} rotate={2}>Friends</Stamp>
          <Stamp color={TOKENS.maroon} rotate={-2}>Teachers</Stamp>
          <Stamp color={TOKENS.orange} rotate={4}>Hokies</Stamp>
        </div>
      </div>
    </div>
  );
}
